import type { ComponentProps } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * The HyperTek prototype's badge (h-prototype `components/ui/badge.tsx`): its base recipe and its four
 * variants, class for class. This product's deltas, and only these:
 *
 * - a `span`, not a `div`, so a badge can sit inside a sentence or a table cell;
 * - `w-fit gap-1.5 whitespace-nowrap`, so the pill keeps its shape in a stacked cell and StatusBadge's dot
 *   stands clear of the word;
 * - five status tones on `app-tokens.css`: a darker same-hue text on a pale tint, each pair >= 4.5:1.
 */
const badgeVariants = cva(
  'inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary: 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
        neutral: 'border-transparent bg-[hsl(var(--h-muted))] text-[hsl(var(--h-muted-foreground))]',
        success: 'border-transparent bg-[hsl(var(--h-success-soft))] text-[hsl(var(--h-success-fg))]',
        warning: 'border-transparent bg-[hsl(var(--h-warning-soft))] text-[hsl(var(--h-warning-fg))]',
        danger: 'border-transparent bg-[hsl(var(--h-error-soft))] text-[hsl(var(--h-error-fg))]',
        info: 'border-transparent bg-[hsl(var(--h-info-soft))] text-[hsl(var(--h-info-fg))]',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

type BadgeProps = ComponentProps<'span'> & VariantProps<typeof badgeVariants>;

function Badge({ className, variant, ...props }: BadgeProps) {
  return <span data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
export type { BadgeProps };
